import React, { useEffect, useMemo, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { X, HelpCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { fetchBusinessHelpTopics } from '../services/contentService';

const toPascalCase = (value = '') =>
  value
    .toString()
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const resolveTopicIcon = (iconName) => {
  if (!iconName) return HelpCircle;
  const pascal = toPascalCase(iconName);
  return LucideIcons[pascal] || LucideIcons[`${pascal}Icon`] || HelpCircle;
};

const QuickOptionsPopup = ({ 
  showQuickOptionsPopup, 
  setShowQuickOptionsPopup, 
  onQuickOptionSelect 
}) => {
  const { theme } = useTheme();
  const { t, currentLanguage } = useLanguage();
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const translate = (key, fallback) => {
    try {
      const value = t(key);
      if (!value || value === key) {
        return fallback;
      }
      return value;
    } catch (err) {
      return fallback;
    }
  };

  useEffect(() => {
    if (!showQuickOptionsPopup) {
      return;
    }

    let isActive = true;

    const loadTopics = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchBusinessHelpTopics();
        if (!isActive) return;
        setTopics(data);
      } catch (err) {
        if (!isActive) return;
        console.error('❌ Failed to load Business Help Topics:', err);
        setError(err?.message || 'Failed to load Business Help Topics');
        setTopics([]);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadTopics();

    return () => {
      isActive = false;
    };
  }, [showQuickOptionsPopup, reloadToken]);

  useEffect(() => {
    if (showQuickOptionsPopup) {
      return;
    }
    setError(null);
  }, [showQuickOptionsPopup]);

  const handleRetry = () => {
    setReloadToken((token) => token + 1);
  };

  const getTopicTitle = (name) => {
    if (!name) return name;
    const key = `quickOptions.topicTitle_${name}`;
    const value = t(key);
    return (value && value !== key) ? value : name;
  };
  const getTopicDescription = (name, defaultDesc) => {
    if (!name) return defaultDesc;
    const key = `quickOptions.topicDesc_${name}`;
    const value = t(key);
    return (value && value !== key) ? value : (defaultDesc || '');
  };

  const topicCards = useMemo(() => {
    return topics.map((topic, index) => {
      const helpWith = translate('quickOptions.iNeedHelpWith', 'I need help with');
      const guidance = translate('quickOptions.canYouProvideGuidance', 'Can you provide guidance?');
      const prompt = `${helpWith} ${topic.name}. ${guidance}`;
      const fallbackColor = `hsl(${(index * 57) % 360}, 70%, 45%)`;
      const displayName = getTopicTitle(topic.name);
      const displayDescription = getTopicDescription(topic.name, topic.description);
      return {
        id: topic.id,
        name: topic.name,
        displayName,
        displayDescription,
        description: topic.description,
        icon: topic.icon,
        IconComponent: resolveTopicIcon(topic.icon),
        sortOrder: topic.sortOrder,
        photoUrl: topic.photo?.url || '',
        prompt,
        color: fallbackColor
      };
    });
  }, [topics, t, currentLanguage]);

  const handleTopicClick = (topic) => {
    console.log('🔍 Business Help Topic clicked:', topic.id);
    if (onQuickOptionSelect) {
      onQuickOptionSelect({
        id: topic.id,
        label: topic.name,
        description: topic.description,
        action: 'prompt',
        prompt: topic.prompt,
        topic
      });
    }
    setShowQuickOptionsPopup(false);
  };

  if (!showQuickOptionsPopup) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: theme.modalOverlay,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '32px'
    }}>
      <div style={{
        background: theme.modalBg,
        borderRadius: '24px',
        maxWidth: 'min(1080px, 96vw)',
        width: '100%',
        maxHeight: '88vh',
        border: `1px solid ${theme.modalBorder}`,
        boxShadow: `0 20px 40px ${theme.primaryShadow}`,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '32px 32px 24px 32px',
          borderBottom: `1px solid ${theme.primaryBorder}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: theme.primaryText,
                margin: 0
              }}>{t('quickOptions.businessHelpTopics')}</h2>
              <p style={{
                fontSize: '0.9rem',
                color: theme.secondaryText,
                margin: '8px 0 0 0',
                fontWeight: '400'
              }}>
                {t('quickOptions.chooseFromTopics')}
              </p>
            </div>
            <button
              onClick={() => setShowQuickOptionsPopup(false)}
              style={{
                background: theme.closeButtonBg,
                border: `1px solid ${theme.closeButtonBorder}`,
                color: theme.closeButtonText,
                padding: '8px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = theme.closeButtonHover;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = theme.closeButtonBg;
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="quick-options-content" style={{
          flex: 1,
          overflow: 'auto',
          padding: '32px'
        }}>
          {isLoading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 16,
              color: theme.secondaryText
            }}>
              <div className="spinner" style={{
                width: 40,
                height: 40,
                border: `4px solid ${theme.primaryBorder}`,
                borderTopColor: theme.accentButton,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{ fontWeight: 600 }}>{translate('quickOptions.loadingTopics', 'Loading topics...')}</div>
            </div>
          )}

          {!isLoading && error && (
            <div style={{
              background: theme.errorBg || 'rgba(248,113,113,0.12)',
              border: `1px solid ${theme.errorBorder || 'rgba(248,113,113,0.4)'}`,
              color: theme.errorText || '#ef4444',
              borderRadius: 16,
              padding: 24,
              textAlign: 'center'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>{translate('quickOptions.topicsLoadFailed', 'Unable to load topics')}</div>
              <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>{error}</div>
              <button
                onClick={handleRetry}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '10px 18px',
                  borderRadius: 999,
                  border: `1px solid ${theme.primaryBorder}`,
                  background: theme.accentButton,
                  color: theme.buttonText || '#0f172a',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '0.85';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {translate('quickOptions.retryLoading', 'Try again')}
              </button>
            </div>
          )}

          {!isLoading && !error && topicCards.length === 0 && (
            <div style={{
              background: theme.cardBg,
              border: `1px solid ${theme.primaryBorder}`,
              borderRadius: 16,
              padding: 32,
              textAlign: 'center',
              color: theme.secondaryText
            }}>
              {translate('quickOptions.noTopicsFound', 'No Business Help Topics found.')}
            </div>
          )}

          {!isLoading && !error && topicCards.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {topicCards.map((topic) => {
                return (
                  <button
                    key={topic.id}
                    onClick={() => handleTopicClick(topic)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      padding: 0,
                      background: theme.cardBg,
                      border: `1px solid ${theme.primaryBorder}`,
                      borderRadius: '20px',
                      color: theme.primaryText,
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      overflow: 'hidden',
                      textAlign: 'left',
                      minHeight: '260px',
                      boxShadow: 'none' // No shadow circle effect
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'; // Simple shadow, no circle effect
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: 160,
                      background: `linear-gradient(135deg, ${topic.color}40, ${topic.color}80)`
                    }}>
                      {topic.photoUrl ? (
                        <img
                          src={topic.photoUrl}
                          alt={topic.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.nextSibling;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: topic.photoUrl ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 8,
                        color: '#fff'
                      }}>
                        <topic.IconComponent size={40} aria-hidden="true" strokeWidth={1.75} />
                      </div>
                    </div>

                    <div style={{ padding: '20px 22px 22px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <h3 style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: theme.primaryText,
                        margin: 0
                      }}>
                        {topic.displayName}
                      </h3>
                      {(topic.displayDescription || topic.description) && (
                        <p style={{
                          fontSize: 14,
                          color: theme.secondaryText,
                          lineHeight: 1.5,
                          margin: 0
                        }}>
                          {topic.displayDescription || topic.description}
                        </p>
                      )}
                      {!topic.displayDescription && !topic.description && (
                        <p style={{
                          fontSize: 14,
                          color: theme.secondaryText,
                          lineHeight: 1.5,
                          margin: 0,
                          opacity: 0.8
                        }}>
                          {translate('quickOptions.getHelpWith', 'Get help with')} {topic.displayName.toLowerCase()}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px 32px',
          borderTop: `1px solid ${theme.primaryBorder}`,
          background: theme.modalBg
        }}>
          <p style={{
            fontSize: '14px',
            color: theme.secondaryText,
            margin: 0,
            opacity: 0.7
          }}>
            {t('quickOptions.clickAnyTopic')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickOptionsPopup;
